import { Immutable } from "immer";
import React from "react";
import { getReactivatedContext } from "tsi-common-react/src/utils/environment";
import { CAPTION_LIGHT_CLASS_NAME } from "tsi-common-react/src/utils/typography";
import { t } from "ttag";

import { Context, templates } from "@reactivated";

import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";
import { richT } from "@thelabnyc/thelabui/src/utils/richText";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { buildComparePageBodyComponentsMap } from "../../blocks/componentMaps";
import {
    CocoonWistiaPopoverEmbed,
    useCocoonWistiaPopoverVideo,
} from "../CocoonVideoBarBlock";
import {
    ComparePageLegacyBodySegment,
    filterComparePageBodySegment,
} from "./utils";

import styles from "./index.module.scss";

type BodyBlock =
    templates.ComparePageVariantCTemplate["page"]["body"]["value"][number];

const useStaticURL = (): string => {
    const context = React.useContext(Context) || getReactivatedContext();
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    return context?.STATIC_URL ?? "/static/";
};

const getStaticURL = (staticURL: string, path: string): string =>
    `${staticURL}${path}`;

const compareVideoAssets = [
    {
        start: "0",
        path: "img/pdp/moving.jpg",
        alt: "Watch Moving",
        caption: "Moving the box",
    },
    {
        start: "10",
        path: "img/pdp/unboxing.jpg",
        alt: "Watch Unboxing",
        caption: "Unpacking the box",
    },
    {
        start: "24",
        path: "img/pdp/unrolling.jpg",
        alt: "Watch Unrolling",
        caption: "Unrolling the mattress",
    },
    {
        start: "47",
        path: "img/pdp/feel.jpg",
        alt: "Watch Feel",
        caption: "trying out the mattress",
    },
] as const;

const ComparePageBodySegment = (props: {
    blocks: Immutable<BodyBlock[]>;
    segment: ComparePageLegacyBodySegment;
    pageSlug: string;
}): React.JSX.Element => {
    const components = buildComparePageBodyComponentsMap({
        pageSlug: props.pageSlug,
    });
    return (
        <StreamField
            tagName={React.Fragment}
            value={filterComparePageBodySegment(props.blocks, props.segment)}
            components={components}
        />
    );
};

const CompareVideoSection = (props: {
    includeQuotes: boolean;
}): React.JSX.Element => {
    const staticURL = useStaticURL();
    const { embedRef, showVideo } = useCocoonWistiaPopoverVideo();

    return (
        <section
            className={concatClassNames([
                styles.compareSectionBlock,
                styles.compareVideoBar,
            ])}
        >
            <CocoonWistiaPopoverEmbed embedRef={embedRef} />
            <div className="main-container">
                <h2
                    className={styles.blockTitle}
                >{t`A new way to buy a mattress`}</h2>
                <p
                    className={concatClassNames([
                        styles.blockSubtitle,
                        CAPTION_LIGHT_CLASS_NAME,
                    ])}
                >
                    {t`From unboxing to relaxing, our owners love how easy it is to get a premium memory foam mattress shipped to their door`}
                </p>
                <section>
                    <article id="mattress-videos">
                        {compareVideoAssets.map((video) => (
                            <div
                                className={styles.compareVideo}
                                data-start={video.start}
                                tabIndex={0}
                                key={video.start}
                                onClick={() => {
                                    showVideo(video.start);
                                }}
                                onKeyDown={(event) => {
                                    if (
                                        event.code === "Space" ||
                                        event.code === "Enter"
                                    ) {
                                        event.preventDefault();
                                        showVideo(video.start);
                                    }
                                }}
                            >
                                <img
                                    src={getStaticURL(staticURL, video.path)}
                                    alt={video.alt}
                                    loading="lazy"
                                    decoding="async"
                                />
                                <p>{video.caption}</p>
                            </div>
                        ))}
                    </article>
                </section>
                {props.includeQuotes ? (
                    <>
                        <p className={styles.compareQuoteHeader}>
                            {t`What our owners are saying about the process`}
                        </p>
                        <div className={styles.compareQuotes}>
                            <div
                                className={concatClassNames([
                                    styles.compareQuote,
                                    "compare-quote",
                                ])}
                            >
                                <h1 className="quote-header">
                                    {t`“UPS brought the box right to my door”`}
                                </h1>
                                <p className="name">{t`Andrew`}</p>
                                <p className="place">{t`San Francisco, CA`}</p>
                            </div>
                            <div className="divider" />
                            <div
                                className={concatClassNames([
                                    styles.compareQuote,
                                    "compare-quote",
                                ])}
                            >
                                <h1 className="quote-header">
                                    {t`“It was easy for me to move the mattress into the bedroom”`}
                                </h1>
                                <p className="name">
                                    {t`Joshua the personal trainer`}
                                </p>
                                <p className="place">{t`San Jose, CA`}</p>
                            </div>
                            <div className="divider" />
                            <div
                                className={concatClassNames([
                                    styles.compareQuote,
                                    "compare-quote",
                                ])}
                            >
                                <h1 className="quote-header">
                                    {t`“I was amazed how the mattress came to life in a few minutes”`}
                                </h1>
                                <p className="name">{t`Jeremy Kettle`}</p>
                                <p className="place">{t`Las Vegas, NV`}</p>
                            </div>
                        </div>
                        <div>
                            <a
                                className={concatClassNames([
                                    styles.videoLink,
                                    "al-compare__link--reviews",
                                ])}
                                href="/reviews"
                            >
                                {t`read more reviews`}
                            </a>
                        </div>
                    </>
                ) : null}
            </div>
        </section>
    );
};

const ComparePromiseSection = (props: {
    variant: "standard" | "purple";
}): React.JSX.Element => {
    const staticURL = useStaticURL();
    const mattressImage =
        props.variant === "purple"
            ? "img/compare-purple/hands_mattress.png"
            : "img/compare/hands_mattress.jpg";
    const rollersImage =
        props.variant === "purple"
            ? "img/compare-purple/rollers.png"
            : "img/compare/rollers.jpg";

    return (
        <section
            className={concatClassNames([
                styles.promiseBlock,
                styles.compareSectionBlock,
            ])}
        >
            <div className="main-container">
                <h2 className={styles.blockTitle}>
                    {richT(
                        t`Backed by our <strong>hassle free</strong> promise`,
                        {
                            strong: (children) => <strong>{children}</strong>,
                        },
                    )}
                </h2>
                <p
                    className={concatClassNames([
                        styles.blockSubtitle,
                        CAPTION_LIGHT_CLASS_NAME,
                    ])}
                >
                    {t`With over 40 million mattresses made in 130 years, our history of quality offers owners the comfort of knowing their Cocoon by Sealy comes from this legacy`}
                </p>
                <div className={styles.promiseRow}>
                    <div className={styles.promiseTile}>
                        <div>
                            <img
                                src={getStaticURL(
                                    staticURL,
                                    "img/compare/dog_box.jpg",
                                )}
                                className={styles.tileImg}
                                alt="Cocoon by Sealy Box and Dog"
                                loading="lazy"
                                decoding="async"
                            />
                        </div>
                        <div>
                            <img
                                src={getStaticURL(
                                    staticURL,
                                    "img/homepage/order-process-step-4.png",
                                )}
                                className={styles.tileIcon}
                                alt="Free Shipping"
                                loading="lazy"
                                decoding="async"
                            />
                        </div>
                        <h5>{t`free shipping`}</h5>
                        <p className={CAPTION_LIGHT_CLASS_NAME}>
                            {t`Your Cocoon by Sealy memory foam mattress is compressed and shipped in an easy-to-move 15” x 15” x 44” box`}
                        </p>
                    </div>
                    <div className={styles.promiseTile}>
                        <img
                            src={getStaticURL(staticURL, mattressImage)}
                            className={styles.tileImg}
                            alt="Cocoon by Sealy Mattress"
                            loading="lazy"
                            decoding="async"
                        />
                        <img
                            src={getStaticURL(
                                staticURL,
                                "img/compare/return_icon.png",
                            )}
                            className={styles.tileIcon}
                            alt="Free Returns"
                            loading="lazy"
                            decoding="async"
                        />
                        <h5>{t`free returns`}</h5>
                        <p className={CAPTION_LIGHT_CLASS_NAME}>
                            {t`If you don’t love your mattress, we’ll refund your money immediately, pick it up for free, and donate it to a local charity –`}{" "}
                            <strong>{t`no questions asked`}</strong>.
                            <br />
                            {t`And don’t worry, you don’t even have to put it back in the box.`}
                        </p>
                    </div>
                    <div className={styles.promiseTile}>
                        <img
                            src={getStaticURL(staticURL, rollersImage)}
                            className={styles.tileImg}
                            alt="Cocoon by Sealy Mattress Test"
                            loading="lazy"
                            decoding="async"
                        />
                        <img
                            src={getStaticURL(
                                staticURL,
                                "img/compare/warranty.png",
                            )}
                            className={styles.tileIcon}
                            alt="10 Year Warranty"
                            loading="lazy"
                            decoding="async"
                        />
                        <h5>{t`10 year warranty`}</h5>
                        <p className={CAPTION_LIGHT_CLASS_NAME}>
                            {t`With over 130 years of experience, you can trust that we’ll still be here 10 years from now to stand behind our products.`}
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
};

export const ComparePageLegacyBody = (
    props: Immutable<{
        page: templates.ComparePageVariantCTemplate["page"];
        layout: "legacy_c" | "legacy_purple";
    }>,
): React.JSX.Element => {
    const body = props.page.body.value;
    const bodySegment = (segment: ComparePageLegacyBodySegment) => (
        <ComparePageBodySegment
            blocks={body}
            segment={segment}
            pageSlug={props.page.slug}
        />
    );

    if (props.layout === "legacy_c") {
        return (
            <>
                {bodySegment("legacy_c_top")}
                <CompareVideoSection includeQuotes={true} />
                <ComparePromiseSection variant="standard" />
                {bodySegment("legacy_c_bottom")}
            </>
        );
    }

    return (
        <>
            {bodySegment("legacy_purple_top")}
            <CompareVideoSection includeQuotes={false} />
            <ComparePromiseSection variant="purple" />
            {bodySegment("legacy_purple_bottom")}
        </>
    );
};
